import React, { FC, useState } from 'react';
import styled from '@emotion/styled';

import { PopoutMenu, PopoutMenuItem } from '~Scenes/Handy/Components/PopoutMenu';
import { Button } from '~Styles';
import MoreHorizIcon from '~/Assets/icons/more_horiz.svg';
import { useButton, useI18n, useLicenses, useSelector, useStaffCan } from '~Hooks';
import { useTableContext } from '~Scenes/Handy/Context/TableContext';
import { useChequeContext } from '~Scenes/Handy/Context/ChequeContext';
import Icon from '~ComponentsImproved/Icon';
import PersonIcon from '~Assets/icons/person.svg';
import EditIcon from '~/Assets/icons/create.svg';
import PersonEditIcon from '~Assets/icons/person-edit.svg';
import SwapIcon from '~/Assets/icons/swap.svg';
import { useStaffContext } from '~Scenes/Handy/Context/StaffProvider';
import { useTableUniverse } from '~Components/TableUniverse';
import useModal from '~ComponentsImproved/Modal/useModal';
import LinkUser from '~Components/LinkUser';
import ChangeChequeOwner from '~Scenes/Tables/Components/Footer/Components/ChangeChequeOwner';
import RenameCheque from '~Scenes/Tables/Components/Footer/Components/RenameCheque';

const TriggerButton = styled(Button)`
  z-index: 3;
  width: 64px;
`;

const EditChequeActions: FC = () => {
  const { i18n } = useI18n();
  const { selectedTable } = useTableContext();
  const { selectedCheque } = useChequeContext();
  const { authedStaff } = useStaffContext();
  const universe = useTableUniverse();
  const licenses = useLicenses();
  const linkCustomerModal = useModal();
  const changeOwnerModal = useModal();
  const renameChequeModal = useModal();
  const staffCan = useStaffCan();

  // Used to determine if we should disable Invoice Print in this menu
  const hasInvoiceMethod = useSelector(
    (state) => !!state.POSPaymentMethods.find((pM) => pM.id === 'PRINTED_INVOICE'),
  );
  const deviceWakeState = useSelector((state) => state.deviceWakeState);

  const [actionMenuOpen, setActionMenuOpen] = useState<boolean>(false);

  // The first cheque on the table is used as a fallback when we do not have a selected cheque
  const firstChequeOnTable = universe.cheques.find(
    (cheque) => cheque.tableId === selectedTable?.id,
  );

  const chequeToEdit = selectedCheque ?? firstChequeOnTable;

  const staffCanMutateGivenCheque = staffCan.mutateCheque(chequeToEdit?.id || '');

  const triggerButton = useButton({
    onClick: () => setActionMenuOpen((prev) => !prev),
  });

  const memberButton = useButton({
    onClick: () => linkCustomerModal.show(),
    disabled: deviceWakeState.syncingTableEvents,
    disabledToast: i18n('Handy.Tables.LoadingEvents'),
  });

  const changeOwnerButton = useButton({
    onClick: () => changeOwnerModal.show(),
    disabled: deviceWakeState.syncingTableEvents || !staffCanMutateGivenCheque,
    disabledToast: deviceWakeState.syncingTableEvents
      ? i18n('Handy.Tables.LoadingEvents')
      : undefined,
  });

  const renameButton = useButton({
    onClick: () => renameChequeModal.show(),
    disabled: deviceWakeState.syncingTableEvents || !staffCanMutateGivenCheque,
    disabledToast: deviceWakeState.syncingTableEvents
      ? i18n('Handy.Tables.LoadingEvents')
      : undefined,
  });

  if (!selectedTable) {
    console.warn('EditChequeActions may only be rendered when there is a selected table.');
    return null;
  }

  if (!chequeToEdit) {
    // This can happen if there are no cheques on the table
    return null;
  }

  if (!staffCanMutateGivenCheque && !licenses.hasApp) {
    // If the staff cannot mutate the cheque and we don't have the app license, there's nothing to show here
    return null;
  }

  return (
    <>
      <div id="actions-button" style={{ position: 'relative' }}>
        <TriggerButton padding="none" background="colorBlueGray50" {...triggerButton}>
          <Icon src={MoreHorizIcon} color="var(--colorBlueGray1100)" />
        </TriggerButton>

        <PopoutMenu
          position={selectedCheque ? 'right' : 'left'}
          open={actionMenuOpen}
          rootId="actions-button"
          closeOnClickOutside
          onClose={() => setActionMenuOpen(false)}>
          {licenses.hasApp && (
            <PopoutMenuItem {...memberButton}>
              <Icon src={chequeToEdit?.userId ? PersonEditIcon : PersonIcon} />
              {i18n('CustomerForCheckout.Member')}
            </PopoutMenuItem>
          )}

          <PopoutMenuItem {...changeOwnerButton}>
            <Icon src={SwapIcon} />
            {i18n('TM.ChangeOwner')}
          </PopoutMenuItem>

          <PopoutMenuItem {...renameButton}>
            <Icon src={EditIcon} />
            {i18n('TM.Rename')}
          </PopoutMenuItem>
        </PopoutMenu>
      </div>

      <LinkUser
        isShowing={linkCustomerModal.isShowing}
        onClose={linkCustomerModal.hide}
        selectedCheque={chequeToEdit.id}
        selectedChequeUserId={chequeToEdit?.userId || null}
      />

      <ChangeChequeOwner
        isShowing={changeOwnerModal.isShowing}
        onClose={changeOwnerModal.hide}
        selectedCheque={chequeToEdit.id}
      />

      <RenameCheque
        isShowing={renameChequeModal.isShowing}
        onClose={renameChequeModal.hide}
        selectedCheque={chequeToEdit.id}
      />
    </>
  );
};

export default EditChequeActions;
